import type { CheckoutInitTokenTransferResponse } from '@funkit/api-base'
import { getSupportedAssets } from '@funkit/api-base'
import {
  isTransferChainEnabled,
  resolveTokenTransferConfig,
} from '@funkit/connect-core'
import { useDynamicConfig } from '../../statsig/useDynamicConfig'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useFunkitIdentity } from '../../identity/IdentityContext'
import { logger } from '../../logging/logger'
import {
  type SupportedToken,
  type TokenChainFilter,
  buildSupportedTokenList,
} from '../supportedTokens'

export interface UseSupportedTokensResult {
  /** Curated deposit tokens (no icons attached), priority symbols first. */
  tokens: SupportedToken[]
  /** `All` + one chip per configured chain. */
  chainFilters: TokenChainFilter[]
  isLoading: boolean
}

/**
 * Fetch + resolve the deposit token list the same way the web SDK does: the
 * curated Statsig `tokentransferconfig` (chains + asset addresses), filtered by
 * the disabled list and merged with `@funkit/api-base`'s `getSupportedAssets`
 * metadata (`resolveTokenTransferConfig`, shared via connect-core) — NOT the raw
 * allow-list. Identity (apiKey) comes from {@link useFunkitIdentity}; the query
 * is disabled until an apiKey is present.
 *
 * `transferInit` gates which chains are offered: Solana / Bitcoin / Tron only
 * appear once the deposit-init response carries their address (mirrors web's
 * `useEnabledTokenTransferChainTokens` via the shared `isTransferChainEnabled`).
 */
export function useSupportedTokens({
  enabled = true,
  transferInit,
}: {
  enabled?: boolean
  transferInit?: CheckoutInitTokenTransferResponse
} = {}): UseSupportedTokensResult {
  const { apiKey } = useFunkitIdentity()
  const tokenTransferConfig = useDynamicConfig('tokentransferconfig')
  const disabledChainAssets = useDynamicConfig(
    'disabletokentransferchainsandassets',
  )

  const { data, isLoading } = useQuery({
    queryKey: ['getSupportedAssets', apiKey] as const,
    queryFn: ({ queryKey: [, key] }) =>
      getSupportedAssets({ apiKey: key, logger }),
    enabled: enabled && !!apiKey,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  })

  const { tokens, chainFilters } = useMemo(() => {
    if (!data) {
      return { tokens: [], chainFilters: [{ id: 'all', label: 'All' }] }
    }
    const resolved = resolveTokenTransferConfig({
      chainConfigs: tokenTransferConfig.chains ?? [],
      priorityTokens: tokenTransferConfig.priorityTokens ?? [],
      disabledChainAssets,
      supportedAssets: data,
      logger,
    })
    const built = buildSupportedTokenList(resolved)

    // Hide chains whose deposit address hasn't been minted (Solana/Bitcoin/Tron)
    // — leaving EVM chains always enabled. The `all` chip is always kept.
    const isEnabled = (chainId: number | undefined) =>
      chainId == null || isTransferChainEnabled(chainId, transferInit)
    return {
      tokens: built.tokens.filter((t) => isEnabled(t.chainId)),
      chainFilters: built.chainFilters.filter(
        (c) => c.id === 'all' || isEnabled(Number(c.id)),
      ),
    }
  }, [data, tokenTransferConfig, disabledChainAssets, transferInit])

  return { tokens, chainFilters, isLoading }
}
